import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from '../components/ContactFormPopup';
import { Consultation } from '../components/Consultation';
import { CourseHowItWorks } from '../components/CourseHowItWorks';
import { ChevronDownIcon, CheckIcon, ArrowRightIcon, BotIcon, BanknoteIcon, GlobeIcon, ZapIcon, TargetIcon, WrenchIcon, MessageCircleIcon, ClipboardListIcon, StarIcon, QuoteIcon, SparklesIcon, RocketIcon, XCircleIcon, CheckCircle2Icon } from 'lucide-react';
export function AiCoursePage() {
  const {
    t,
    language
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      if (diff > 0) {
        setTimeLeft({
          hours: Math.floor(diff / (1000 * 60 * 60) % 24),
          minutes: Math.floor(diff / 1000 / 60 % 60),
          seconds: Math.floor(diff / 1000 % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  // Calculator State
  const [pricePerSite, setPricePerSite] = useState(250);
  const [sitesPerMonth, setSitesPerMonth] = useState(10);
  const monthlyIncome = pricePerSite * sitesPerMonth;
  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const handleConsultationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPopupOpen(true);
  };
  const handleBuyClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsPopupOpen(true);
  };
  // Refs for animations
  const whyRef = useRef(null);
  const isWhyInView = useInView(whyRef, {
    once: true,
    margin: '-100px'
  });
  const faqRef = useRef(null);
  const isFaqInView = useInView(faqRef, {
    once: true,
    margin: '-100px'
  });
  return <div className="min-h-screen w-full" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-403" data-mp="33pvu6q58amop">
      <Navigation data-id="element-404" data-mp="3hode0cs0jf1c" />

      <main data-id="element-405" data-mp="3hmsurjb5fiis">
        {/* SECTION 1 — HERO */}
        <section className="relative w-full overflow-hidden px-4 pt-24 md:pt-28 pb-12 md:pb-16 flex flex-col min-h-[calc(100vh-80px)] justify-center" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-406" data-mp="3cpixjhhiq2ke">
          {/* Subtle background accent */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-407" data-mp="3tyl9jm86v61k">
            <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px]" style={{
            background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
          }} data-id="element-408" data-mp="2stsrdgax5rf" />
          </div>

          {/* Smooth gradient fade at the bottom to prevent sharp cutoff */}
          <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none z-10" style={{
          background: 'linear-gradient(to top, var(--bg-primary), transparent)'
        }} data-id="element-409" data-mp="3b7bz9aqlgd8x" />

          <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mt-0 md:mt-8 lg:mt-0" data-id="element-410" data-mp="368quqz4b77eo">
            {/* Left side - Content */}
            <div className="w-full md:flex-1 text-center md:text-left" data-id="element-411" data-mp="14iffpko0nqke">
              {/* Badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 md:mb-6 border" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)',
              color: 'var(--text-secondary)'
            }} data-id="element-412" data-mp="30llj2sobb2nc">
                <span className="text-xs md:text-sm font-semibold tracking-wide flex items-center gap-1.5" data-id="element-413" data-mp="28rru04pqx1fj">
                  🔥 {t('course.hero.badge')}
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="mb-4 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold" style={{
              color: 'var(--text-primary)'
            }} data-id="element-414" data-mp="1twdcnua6e5d4">
                {t('course.hero.title')}
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.25
            }} className="mb-6 md:mb-8 text-lg md:text-xl" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-415" data-mp="162bjo75kv397">
                {language === 'ua' ? 'Ти отримуєш не курс і не інструмент — ти отримуєш повністю зібрану екосистему для розробки сайтів.' : language === 'ru' ? 'Ты получаешь не курс и не инструмент — ты получаешь полностью собранную экосистему для разработки сайтов.' : 'You get not a course and not a tool — you get a fully assembled ecosystem for website development.'}
              </motion.p>

              {/* ========================================== */}
              {/* DESKTOP ONLY ELEMENTS (Hidden on Mobile)   */}
              {/* ========================================== */}

              {/* Features / Pills - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="hidden md:flex flex-wrap items-center justify-start gap-3 mb-8 mt-4" data-id="element-416" data-mp="1co6ajt5qli9">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-417" data-mp="1u76zko54ptb0" data-mpi={i}>
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-418" data-mp="21yp5r1g2i2i1" />

                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-419" data-mp="1s8vvq4vkpvfw">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>

              {/* Pricing & CTA - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="hidden md:flex w-max flex-row items-center gap-8 bg-white rounded-[2rem] p-3 pr-3 pl-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 mt-6" data-id="element-420" data-mp="2fwm5u7tqcr1g">
                <div className="flex flex-col items-start py-0" data-id="element-421" data-mp="3b6sg8ishu2mz">
                  <span className="text-sm font-semibold mb-1 flex items-center gap-1.5" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-422" data-mp="3b57o0ztnivsa">
                    🤑 {t('course.hero.specialPrice')}
                  </span>
                  <div className="flex items-baseline gap-3" data-id="element-423" data-mp="3txs055l4sm9e">
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-424" data-mp="3tw8knh7auf8o">
                      €9 500
                    </span>
                  </div>
                </div>

                <button onClick={handleBuyClick} className="bg-neutral-900 text-white px-8 py-5 rounded-2xl font-bold text-lg hover:bg-neutral-800 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-425" data-mp="24lgdw4sgckzi">
                  {t('course.hero.buyCourse')}{' '}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-426" data-mp="1g5anuz71xoqj" />
                </button>
              </motion.div>

              {/* ========================================== */}
              {/* MOBILE ONLY ELEMENTS (Hidden on Desktop)   */}
              {/* ========================================== */}

              {/* Mobile Card (Video + Pricing + Social Proof) */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex md:hidden flex-col w-full bg-white rounded-3xl shadow-xl border overflow-hidden mb-6" style={{
              borderColor: 'var(--border)'
            }} data-id="element-427" data-mp="1yw1tbpnx4uzn">
                {/* Video Section */}
                <div className="w-full pt-6 pb-2" data-id="element-428" data-mp="3ocxf1oc51gsy">
                  <video src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full max-w-[240px] mx-auto h-auto mix-blend-multiply" data-id="element-429" data-mp="3oftdrevt89ig" />
                </div>

                <div className="flex flex-col p-5 sm:p-6 pt-0 gap-6" data-id="element-430" data-mp="68lxqs9j1y89">
                  {/* Pricing Section */}
                  <div className="flex flex-col items-center" data-id="element-431" data-mp="13ojdq2uomh4k">
                    <span className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-432" data-mp="ywrivg4htp8m">
                      🤑 {t('course.hero.specialPrice')}
                    </span>
                    <div className="flex items-baseline gap-3 mb-6" data-id="element-433" data-mp="18zn5qobb3vgo">
                      <span className="text-5xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-434" data-mp="1d21oz9iw0x8h">
                        €9 500
                      </span>
                    </div>

                    <button onClick={handleBuyClick} className="w-full bg-neutral-900 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-neutral-800 transition-all shadow-md flex items-center justify-center gap-2 mb-6" data-id="element-435" data-mp="q2c7n71ev82t">
                      {t('course.hero.buyCourse')}{' '}
                      <ArrowRightIcon className="w-5 h-5" data-id="element-436" data-mp="2ognltv6n7xuf" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Pills Section - Mobile (Moved outside) */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="flex md:hidden flex-wrap items-center justify-center gap-2 mb-6 mt-6" data-id="element-437" data-mp="8qouygm6huph">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-438" data-mp="34rsemoka8rvb" data-mpi={i}>
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-439" data-mp="2l204mjhfq2rx" />

                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-440" data-mp="2v060ajcnecoc">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>
            </div>

            {/* Right side - Video (Desktop only) */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="hidden md:flex md:flex-1 items-center justify-center h-full" data-id="element-441" data-mp="1zr2jwp6i7jmy">
              <div className="relative w-full max-w-[480px] mx-auto rounded-3xl overflow-hidden shadow-2xl border bg-white" style={{
              borderColor: 'var(--border)'
            }} data-id="element-442" data-mp="1gsq4wmswvfjg">
                <video src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-443" data-mp="12322tjcwocuo" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — WHY IT'S WORTH IT */}
        <section ref={whyRef} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-444" data-mp="ndz36u949x9v">
          <div className="relative z-10 mx-auto max-w-6xl" data-id="element-445" data-mp="ehhzgc3311o5">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isWhyInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 sm:mb-16 text-center" data-id="element-446" data-mp="125rjb8gqjfcn">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-447" data-mp="1pvc31tkpqgmz">
                {t('course.recognize.title')}
              </h2>
            </motion.div>

            {/* Cards Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-448" data-mp="28ydx65wmsqlr">
              {[{
              emoji: '😤',
              title: t('course.recognize.c1.title'),
              text: t('course.recognize.c1.text')
            }, {
              emoji: '💸',
              title: t('course.recognize.c2.title'),
              text: t('course.recognize.c2.text')
            }, {
              emoji: '😰',
              title: t('course.recognize.c3.title'),
              text: t('course.recognize.c3.text')
            }, {
              emoji: '⏳',
              title: t('course.recognize.c4.title'),
              text: t('course.recognize.c4.text')
            }].map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isWhyInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} data-id="element-449" data-mp="uy0zhgah64i8" data-mpi={index}>
                  <div className="h-full rounded-3xl border p-6 sm:p-8 flex flex-col items-start gap-4" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-450" data-mp="1ms33e1oq5l3v">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-3xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-451" data-mp="3ti9bnqc9m3md">
                      {step.emoji}
                    </div>
                    <div data-id="element-452" data-mp="3c62ou5dmj65k">
                      <h3 className="mb-2 text-xl font-bold leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-453" data-mp="3792g1mpe3par">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-454" data-mp="3uydaynk26pgz">
                        {step.text}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 2.5 — WHAT YOU WILL LEARN */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-455" data-mp="1fv95ozhewoy6">
          <div className="max-w-6xl mx-auto" data-id="element-456" data-mp="1644pxxjwx02a">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-457" data-mp="121zwaytgs3fn">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-458" data-mp="1krzjfg5qv8gf">
                {t('course.offer.title')}
              </h2>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="relative w-full" data-id="element-459" data-mp="2l1qty4veyyg9">
              {/* Desktop Unified Background */}
              <div className="hidden lg:block absolute inset-0 rounded-[2.5rem] shadow-xl border pointer-events-none overflow-hidden" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)'
            }} data-id="element-460" data-mp="38p79s81ht0qb">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[100px] opacity-20" style={{
                background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
              }} data-id="element-461" data-mp="1jcwusmb21730" />
              </div>

              <div className="relative z-10 flex flex-col p-6 sm:p-8 lg:p-12 xl:p-16" data-id="element-462" data-mp="2zojb00ajrczl">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start mb-12" data-id="element-463" data-mp="2zmezoxabjbpa">
                  {/* Left Card */}
                  <div className="flex-1 flex flex-col justify-center" data-id="element-464" data-mp="39m5s8tfbjgmq">
                    {/* Tags */}
                    <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-3 mb-6 sm:mb-8" data-id="element-465" data-mp="34o4782yc178m">
                      <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border" style={{
                      backgroundColor: 'var(--bg-primary)',
                      borderColor: 'var(--border)'
                    }} data-id="element-466" data-mp="3jcs54fvnht1q">
                        <span className="text-xs sm:text-sm font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-467" data-mp="3nfx07xdeisgc">
                          🌱 {t('course.offer.tag2')}
                        </span>
                      </div>
                    </div>

                    {/* Image + Headline */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6" data-id="element-468" data-mp="3o9o1m53tszfh">
                      <motion.div initial={{
                      opacity: 0,
                      scale: 0.9
                    }} whileInView={{
                      opacity: 1,
                      scale: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.8
                    }} className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl overflow-hidden flex-shrink-0" data-id="element-469" data-mp="3t0mlfeo0wfnh">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/IMG_4595.png" alt="AI Website Creator" className="w-full h-full object-cover scale-110 mix-blend-multiply" data-id="element-470" data-mp="2sj43fangccxr" />
                      </motion.div>
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-471" data-mp="29jq7woa2qu55">
                        {t('course.offer.heading')}
                      </h2>
                    </div>

                    {/* Description */}
                    <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-xl" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-472" data-mp="1lnv9tqaihfvc">
                      {t('course.offer.description')}
                    </p>

                    {/* New Phrase */}
                    <div className="mt-6 sm:mt-8 pt-6 border-t" style={{
                    borderColor: 'var(--border)'
                  }} data-id="element-473" data-mp="12q28utjg7g0l">
                      <p className="text-base sm:text-lg font-medium leading-relaxed" data-id="element-474" data-mp="1bq8rsuxx1875">
                        <span style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-475" data-mp="1gplg7k4fqnbu">
                          {t('course.offer.tagline1')}{' '}
                        </span>
                        <span style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-476" data-mp="2s7391okha2jl">
                          {t('course.offer.tagline2')}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Right Card: Features */}
                  <div className="w-full lg:w-[400px] xl:w-[450px] flex flex-col justify-center" data-id="element-477" data-mp="x2n24l3judwp">
                    <div className="space-y-4" data-id="element-478" data-mp="9ciyweu9wg68">
                      {[{
                      text: t('course.offer.feature1'),
                      icon: GlobeIcon
                    }, {
                      text: t('course.offer.feature2'),
                      icon: SparklesIcon
                    }, {
                      text: t('course.offer.feature3'),
                      icon: BotIcon
                    }, {
                      text: t('course.offer.feature4'),
                      icon: ClipboardListIcon
                    }, {
                      text: t('course.offer.feature5'),
                      icon: WrenchIcon
                    }].map((feature, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-md" style={{
                      borderColor: 'var(--border)',
                      backgroundColor: 'var(--bg-primary)'
                    }} data-id="element-479" data-mp="1s8usre801okq" data-mpi={i}>
                          <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border" style={{
                        backgroundColor: 'var(--bg-secondary)',
                        borderColor: 'var(--border)'
                      }} data-id="element-480" data-mp="2c1158d7uws5p">
                            <feature.icon className="w-5 h-5" style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-481" data-mp="1n7gfz7yzndil" />
                          </div>
                          <span className="text-sm sm:text-base font-semibold" style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-482" data-mp="2awslnxv3x3g5">
                            {feature.text}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Price and Button */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t" style={{
                borderColor: 'var(--border)'
              }} data-id="element-483" data-mp="2ow0mev6x4jmg">
                  <div className="flex flex-col items-center sm:items-start text-center sm:text-left" data-id="element-484" data-mp="366xiyd4y7d76">
                    <span className="text-4xl sm:text-5xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-485" data-mp="3b60khnlpz1ae">
                      {t('course.offer.price')}
                    </span>
                    <p className="text-sm font-medium mt-2" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-486" data-mp="3oxfaiee1o2qy">
                      {t('course.offer.priceDescription')}
                    </p>
                  </div>

                  <button onClick={handleBuyClick} className="w-full sm:w-auto px-10 py-4 rounded-2xl text-base sm:text-lg font-bold transition-all hover:scale-[1.02] shadow-lg" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-487" data-mp="6r0vsd7ybori">
                    {t('course.offer.cta')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <CourseHowItWorks data-id="element-488" data-mp="1s9io03oq7xtn" />

        {/* SECTION 4 — INCOME CALCULATOR */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-489" data-mp="1x5zdp2bkqk8u">
          <div className="max-w-6xl mx-auto" data-id="element-490" data-mp="1nfxp89fb6zte">
            <div className="text-center mb-12 sm:mb-16" data-id="element-491" data-mp="1neml28hybq1r">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-492" data-mp="2b61uel85b7mi">
                {t('course.calc.title')}
              </h2>
            </div>

            <div className="rounded-[2.5rem] p-6 sm:p-10 lg:p-12 shadow-xl border" style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border)'
          }} data-id="element-493" data-mp="3volznqbkekom">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-494" data-mp="99jgzfpv4fml">
                {/* Left: Sliders */}
                <div className="space-y-12" data-id="element-495" data-mp="3cpgi6z61qd8s">
                  {/* Slider 1 */}
                  <div data-id="element-496" data-mp="3crkx0c5hgwnu">
                    <div className="flex justify-between items-end mb-6" data-id="element-497" data-mp="3qozp5on4fiji">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-498" data-mp="3qn550cjobugh">
                        {t('course.calc.priceLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-499" data-mp="3urvz5b8py0a9">
                        €{pricePerSite}
                      </span>
                    </div>
                    <input type="range" min="100" max="2000" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 100) / (2000 - 100) * 100}%, var(--border) ${(pricePerSite - 100) / (2000 - 100) * 100}%)`
                  }} data-id="element-500" data-mp="cmkd9njidytd" />
                  </div>

                  {/* Slider 2 */}
                  <div data-id="element-501" data-mp="2pt8utx008n75">
                    <div className="flex justify-between items-end mb-6" data-id="element-502" data-mp="2prpg4atzyk7h">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-503" data-mp="2ktntxh19y4rs">
                        {t('course.calc.sitesLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-504" data-mp="26uwkw919r9ai">
                        {sitesPerMonth}
                      </span>
                    </div>
                    <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                  }} data-id="element-505" data-mp="1t4kpo19e0uf5" />
                  </div>
                </div>

                {/* Right: Results */}
                <div className="rounded-3xl p-8 sm:p-10 flex flex-col justify-center relative overflow-hidden" style={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border)'
              }} data-id="element-506" data-mp="30iel13h3sgna">
                  {/* Background glow */}
                  <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full blur-[80px] opacity-10 pointer-events-none" style={{
                  background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
                }} data-id="element-507" data-mp="orsuc8ufc0fy" />

                  <div className="relative z-10" data-id="element-508" data-mp="296pcawizrnbs">
                    <p className="text-sm font-bold mb-2 uppercase tracking-wider" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-509" data-mp="2nyrgutw0b4i5">
                      {t('course.calc.potentialIncome')}
                    </p>
                    <div className="flex items-baseline gap-2 mb-8 flex-wrap" data-id="element-510" data-mp="2ryruicba7bo5">
                      <span className="text-5xl sm:text-6xl lg:text-7xl font-bold break-words" style={{
                      color: 'var(--accent)'
                    }} data-id="element-511" data-mp="36vivgq0lsvfv">
                        €{monthlyIncome.toLocaleString()}
                      </span>
                      <span className="text-base sm:text-lg font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-512" data-mp="17lj548ofiab5">
                        {t('course.calc.perMonth')}
                      </span>
                    </div>

                    <button onClick={handleBuyClick} className="block w-full rounded-2xl py-4 text-center text-lg font-bold transition-all hover:scale-[1.02] shadow-lg mt-10" style={{
                    backgroundColor: 'var(--accent)',
                    color: '#FFFFFF'
                  }} data-id="element-513" data-mp="2b7wdp30hg7ut">
                      {t('course.offer.cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3.5 — FEAR VS REALITY */}
        <section className="hidden relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-514" data-mp="33wtqiqlxdcg6">
          <div className="max-w-5xl mx-auto" data-id="element-515" data-mp="n9cbwv54yy6p">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-12 sm:mb-16" data-id="element-516" data-mp="nborx2rc2yh4">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl leading-tight" style={{
              color: 'var(--text-primary)'
            }} data-id="element-517" data-mp="118bqtb2ujypo">
                {t('course.fear.title1')} <br className="hidden sm:block" data-id="element-518" data-mp="1trr4dsg58vct" />
                {t('course.fear.title2')}
              </h2>
            </motion.div>

            <div className="flex flex-col gap-4 sm:gap-6" data-id="element-519" data-mp="xauwm30zovng">
              {[{
              fear: t('course.fear.f1.fear'),
              reality: t('course.fear.f1.reality')
            }, {
              fear: t('course.fear.f2.fear'),
              reality: t('course.fear.f2.reality')
            }, {
              fear: t('course.fear.f3.fear'),
              reality: t('course.fear.f3.reality')
            }, {
              fear: t('course.fear.f4.fear'),
              reality: t('course.fear.f4.reality')
            }].map((row, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-520" data-mp="1c0ak9a584kyx" data-mpi={i}>
                  {/* Fear Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-red-50/50 border-red-100 flex items-center gap-4" data-id="element-521" data-mp="3t9vnjj6k3lkv">
                    <div className="w-10 h-10 rounded-full bg-red-100/50 flex items-center justify-center flex-shrink-0" data-id="element-522" data-mp="35j9lc8j8gvis">
                      <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-523" data-mp="3f8t135bng99f" />
                    </div>
                    <span className="text-base sm:text-lg text-gray-500 line-through decoration-red-300" data-id="element-524" data-mp="3fanl8hf3jxcg">
                      {row.fear}
                    </span>
                  </div>

                  {/* Reality Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-neutral-50/50 border-neutral-200 flex items-center gap-4 shadow-sm" data-id="element-525" data-mp="2htymiwlg1b7a">
                    <div className="w-10 h-10 rounded-full bg-neutral-100/50 flex items-center justify-center flex-shrink-0" data-id="element-526" data-mp="1j0vgnpz40krj">
                      <CheckIcon className="w-5 h-5 text-neutral-800" data-id="element-527" data-mp="1e5pucq1sp42x" />
                    </div>
                    <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-528" data-mp="1s0sb6886kdui">
                      {row.reality}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 8 — FAQ */}
        <section ref={faqRef} className="relative w-full overflow-hidden px-4 py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-529" data-mp="vb13166yus68">
          <div className="relative z-10 mx-auto max-w-3xl" data-id="element-530" data-mp="14bpjs8nqv6mt">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isFaqInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-531" data-mp="gmn2j1x0vfsu">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-532" data-mp="3toqn799854yn">
                {t('course.faq.title')}
              </h2>
              <p style={{
              color: 'var(--text-secondary)'
            }} data-id="element-533" data-mp="q6p9ijh2t00k">
                {t('course.faq.subtitle')}
              </p>
            </motion.div>

            <div className="space-y-4" data-id="element-534" data-mp="g89ishlynsrl">
              {[{
              q: t('course.faq.q1'),
              a: t('course.faq.a1')
            }, {
              q: t('course.faq.q2'),
              a: t('course.faq.a2')
            }, {
              q: t('course.faq.q3'),
              a: t('course.faq.a3')
            }, {
              q: t('course.faq.q4'),
              a: t('course.faq.a4')
            }, {
              q: t('course.faq.q5'),
              a: t('course.faq.a5')
            }, {
              q: t('course.faq.q6'),
              a: t('course.faq.a6')
            }, {
              q: t('course.faq.q7'),
              a: t('course.faq.a7')
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isFaqInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} data-id="element-535" data-mp="9tkd138bnr03" data-mpi={index}>
                  <div className="rounded-2xl border overflow-hidden" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-536" data-mp="2lhz0mi5ytzkl">
                    <button onClick={() => toggleFaq(index)} className="flex w-full items-center justify-between p-6 text-left transition-colors" aria-expanded={openFaqIndex === index} data-id="element-537" data-mp="1xqtifwcjyv74">
                      <h3 className="pr-4 font-semibold text-base" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-538" data-mp="27n6ev1zhfn5k">
                        {item.q}
                      </h3>
                      <motion.div animate={{
                    rotate: openFaqIndex === index ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className="flex-shrink-0" data-id="element-539" data-mp="3grugvzcqpci2">
                        <ChevronDownIcon className="h-5 w-5" style={{
                      color: 'var(--accent)'
                    }} data-id="element-540" data-mp="3ipp976kcy3a" />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false} data-id="element-541" data-mp="89ppomew5yaj">
                      {openFaqIndex === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-542" data-mp="n6oyuepe5z74">
                          <div className="border-t px-6 pb-6 pt-4" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-543" data-mp="i4dev7vjcutj">
                            <p className="leading-relaxed" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-544" data-mp="e5eapphmp235">
                              {item.a}
                            </p>

                            {/* Duplicate Calculator for Question 5 */}
                            {index === 4 && <div className="mt-8" data-id="element-545" data-mp="3cen18b9zn17o">
                                <div className="rounded-3xl p-6 sm:p-8 border shadow-md" style={{
                          backgroundColor: 'var(--bg-primary)',
                          borderColor: 'var(--border)'
                        }} data-id="element-546" data-mp="37i6cpfyvmku2">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-547" data-mp="3q737dky6rfct">
                                    {/* Left: Sliders */}
                                    <div className="space-y-8" data-id="element-548" data-mp="3lb5n53jsxour">
                                      {/* Slider 1 */}
                                      <div data-id="element-549" data-mp="29tw60no5b8wm">
                                        <div className="flex justify-between items-end mb-4" data-id="element-550" data-mp="29s2zr9hg2qp5">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-551" data-mp="pcwux734pcru">
                                            {t('course.calc.priceLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-552" data-mp="1d04x3g59g5pn">
                                            €{pricePerSite}
                                          </span>
                                        </div>
                                        <input type="range" min="100" max="2000" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 100) / (2000 - 100) * 100}%, var(--border) ${(pricePerSite - 100) / (2000 - 100) * 100}%)`
                                }} data-id="element-553" data-mp="1uhtngjj2xxah" />
                                      </div>

                                      {/* Slider 2 */}
                                      <div data-id="element-554" data-mp="2qh3h51rxofra">
                                        <div className="flex justify-between items-end mb-4" data-id="element-555" data-mp="2qi51jwuuaelo">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-556" data-mp="2ujqbb9c2ucrn">
                                            {t('course.calc.sitesLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-557" data-mp="517tacvjzqoz">
                                            {sitesPerMonth}
                                          </span>
                                        </div>
                                        <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                                }} data-id="element-558" data-mp="1p1eb0f2bixfq" />
                                      </div>
                                    </div>

                                    {/* Right: Results */}
                                    <div className="rounded-2xl p-6 flex flex-col justify-center relative overflow-hidden" style={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border)'
                            }} data-id="element-559" data-mp="3ei9yoja3ltbo">
                                      <div className="relative z-10" data-id="element-560" data-mp="12sz6pa20buol">
                                        <p className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                                  color: 'var(--text-secondary)'
                                }} data-id="element-561" data-mp="2x8eauz5m3a5i">
                                          {t('course.calc.potentialIncome')}
                                        </p>
                                        <div className="flex items-baseline gap-2 mb-6 flex-wrap" data-id="element-562" data-mp="3ky0a1mlzhpdi">
                                          <span className="text-4xl sm:text-5xl font-bold break-words" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-563" data-mp="36yr4dgvemdns">
                                            €{monthlyIncome.toLocaleString()}
                                          </span>
                                          <span className="text-sm font-medium" style={{
                                    color: 'var(--text-muted)'
                                  }} data-id="element-564" data-mp="1n2bn9i8j2qa6">
                                            {t('course.calc.perMonth')}
                                          </span>
                                        </div>

                                        <button onClick={handleBuyClick} className="block w-full rounded-xl py-3 text-center text-sm font-bold transition-all hover:scale-[1.02] shadow-md mt-6" style={{
                                  backgroundColor: 'var(--accent)',
                                  color: '#FFFFFF'
                                }} data-id="element-565" data-mp="1j0ge5rkcv8lu">
                                          {t('course.offer.cta')}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 9 — FINAL CTA (Consultation Component) */}
        <Consultation data-id="element-566" data-mp="2icqd70rij1gx" />
      </main>

      <Footer data-id="element-567" data-mp="2idrw1rerss97" />
      <CookieBanner data-id="element-568" data-mp="2ieb5ejqcu8bm" />

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-569" data-mp="2ieuoi3rqr2r8" />
    </div>;
}